import React, { useEffect, useState } from "react";
import DataGrid, {
	Column,
	Paging,
	FilterRow,
	HeaderFilter,
} from "devextreme-react/data-grid";
import { Popup } from "devextreme-react/popup";
import { Button } from "devextreme-react/button";
import {
	getUserOrders,
	refundUserOrder,
	checkTBankPayment,
	reconcileTBankPayment,
} from "../api/apiServiceFetch";

const OrderPopup = ({ userId, visible, onClose }) => {
	const [orders, setOrders] = useState([]);
	const [refundingId, setRefundingId] = useState(null);
	const [checkingId, setCheckingId] = useState(null);
	const [reconcilingId, setReconcilingId] = useState(null);
	// Статус последней операции (возврат или проверка). Раньше результат
	// возврата выводился сырым JSON через alert — непонятно было «прошло или
	// нет», ответ ТБанка сворачивался в `[object Object]`. Теперь парсим ответ
	// и показываем короткое человеческое сообщение прямо в попапе.
	const [opStatus, setOpStatus] = useState(null);

	useEffect(() => {
		const fetchOrders = async () => {
			if (userId && visible) {
				try {
					const data = await getUserOrders(userId);
					setOrders(data);
				} catch (err) {
					console.error("Ошибка загрузки заказов пользователя:", err);
				}
			}
		};
		fetchOrders();
		// Сбрасываем статус при повторном открытии: показывать прошлую операцию
		// у другого пользователя бессмысленно.
		if (visible) setOpStatus(null);
	}, [userId, visible]);

	// Ответ ТБанка приходит либо строкой-JSON внутри {ok, response}, либо
	// плоским объектом. Приводим к одному виду, чтобы читать Success / Status /
	// ErrorCode без разницы, кто и как завернул.
	const parseTBankResponse = (resp) => {
		if (!resp) return null;
		if (resp.ok === false) {
			return { success: false, errorCode: null, status: null, message: resp.error };
		}
		let payload = resp.response ?? resp;
		if (typeof payload === "string") {
			try {
				payload = JSON.parse(payload);
			} catch {
				return { success: false, errorCode: null, status: null, message: payload };
			}
		}
		if (!payload || typeof payload !== "object") {
			return { success: false, errorCode: null, status: null, message: "Пустой ответ" };
		}
		return {
			success: payload.Success === true || payload.Success === "true",
			errorCode: payload.ErrorCode ?? null,
			status: payload.Status ?? null,
			message: payload.Message || payload.Details || null,
		};
	};

	const handleRefund = async (order) => {
		if (!order?.id) return;
		if (!window.confirm(`Сделать возврат по заказу #${order.id}?`)) return;
		setRefundingId(order.id);
		setOpStatus(null);
		try {
			const resp = await refundUserOrder(order.id);
			const parsed = parseTBankResponse(resp);
			if (parsed?.success) {
				setOpStatus({
					kind: "ok",
					orderId: order.id,
					text:
						"Возврат выполнен" +
						(parsed.status ? ` · статус ${parsed.status}` : ""),
				});
			} else {
				const code = parsed?.errorCode ? ` (код ${parsed.errorCode})` : "";
				const msg = parsed?.message ? `: ${parsed.message}` : "";
				setOpStatus({
					kind: "fail",
					orderId: order.id,
					text: `Возврат не прошёл${code}${msg}`,
				});
			}
			// Независимо от исхода подтягиваем свежий список — у банка возврат
			// иногда ставится не сразу, но строка в UserOrders уже могла
			// получить флаг возврата (у нас он прописан в payState/isDel).
			const data = await getUserOrders(userId);
			setOrders(data);
		} catch (err) {
			setOpStatus({
				kind: "fail",
				orderId: order.id,
				text: `Ошибка возврата: ${err?.message || err}`,
			});
		} finally {
			setRefundingId(null);
		}
	};

	// Ручное подтверждение «висящего» заказа. Повторяет логику колбека:
	// на бэке проверяется Status=CONFIRMED + сумма, и только тогда дёргается
	// ConfirmUserOrderByUuIdAsync. Нужно, когда T-Bank не прислал Notification
	// (чаще всего для SberPay/SBP), а деньги реально списаны. Уже оплаченный
	// заказ (payState=true) бэк не трогает — возвращает alreadyPaid=true.
	const handleReconcile = async (order) => {
		if (!order?.id) return;
		if (!window.confirm(`Подтвердить заказ #${order.id} по статусу T-Bank?`)) return;
		setReconcilingId(order.id);
		setOpStatus(null);
		try {
			const resp = await reconcileTBankPayment(order.id);
			if (resp?.alreadyPaid) {
				setOpStatus({
					kind: "ok",
					orderId: order.id,
					text: "Уже оплачен ранее — ничего не меняли.",
				});
			} else if (resp?.confirmed) {
				setOpStatus({
					kind: "ok",
					orderId: order.id,
					text: `Подтверждён (Status=${resp.status}, Amount=${resp.amount}).`,
				});
			} else {
				const parts = [];
				if (resp?.status) parts.push(`status=${resp.status}`);
				if (resp?.errorCode) parts.push(`code=${resp.errorCode}`);
				if (resp?.message) parts.push(resp.message);
				if (resp?.error) parts.push(resp.error);
				setOpStatus({
					kind: "warn",
					orderId: order.id,
					text: `Не подтверждён${parts.length ? " · " + parts.join(" · ") : ""}`,
				});
			}
			const data = await getUserOrders(userId);
			setOrders(data);
		} catch (err) {
			setOpStatus({
				kind: "fail",
				orderId: order.id,
				text: `Ошибка подтверждения: ${err?.message || err}`,
			});
		} finally {
			setReconcilingId(null);
		}
	};

	// Проверяем реальный статус в T-Bank по сохранённому PaymentId. Нужно,
	// когда юзер говорит «я оплатил, премиум не выдали»: уведомление могло
	// пропасть (сбой у банка, URL уведомлений сбит в ЛК), а у нас заказ висит
	// неоплаченным. /v2/GetState возвращает статус напрямую со стороны банка.
	const handleCheck = async (order) => {
		if (!order?.paymentId) return;
		setCheckingId(order.id);
		setOpStatus(null);
		try {
			const resp = await checkTBankPayment(order.paymentId);
			const parsed = parseTBankResponse(resp);
			const status = parsed?.status || "неизвестно";
			if (parsed?.success && status.toUpperCase() === "CONFIRMED") {
				setOpStatus({
					kind: "ok",
					orderId: order.id,
					text: `В T-Bank подтверждено (CONFIRMED). У нас payState=${order.payState ? "true" : "false"}. Если не совпадает — колбек не доехал.`,
				});
			} else {
				const code = parsed?.errorCode ? ` (код ${parsed.errorCode})` : "";
				const msg = parsed?.message ? ` · ${parsed.message}` : "";
				setOpStatus({
					kind: status.toUpperCase() === "CONFIRMED" ? "ok" : "warn",
					orderId: order.id,
					text: `Статус в T-Bank: ${status}${code}${msg}`,
				});
			}
		} catch (err) {
			setOpStatus({
				kind: "fail",
				orderId: order.id,
				text: `Ошибка проверки: ${err?.message || err}`,
			});
		} finally {
			setCheckingId(null);
		}
	};

	if (!visible) return null;

	return (
		<Popup
			visible={true}
			onHiding={onClose}
			dragEnabled={true}
			showTitle={true}
			title="Заказы пользователя"
			width={1100}
			height={500}
		>
			{opStatus ? (
				<div
					style={{
						marginBottom: 8,
						padding: "8px 12px",
						borderRadius: 6,
						fontWeight: 600,
						color: "#fff",
						background:
							opStatus.kind === "ok"
								? "#2e7d32"
								: opStatus.kind === "warn"
								? "#b26b00"
								: "#b2153f",
					}}
				>
					Заказ #{opStatus.orderId}: {opStatus.text}
				</div>
			) : null}
			<DataGrid
				dataSource={orders}
				keyExpr="id"
				showBorders={true}
				onRowPrepared={(e) => {
					// Неоплаченные подсвечиваем, чтобы сразу видеть «висяки» и
					// понимать, по какой строке стоит нажать «Проверить».
					if (e.rowType === "data" && !e.data.payState) {
						e.rowElement.style.background = "#fff5f5";
					}
				}}
			>
				<Paging defaultPageSize={10} />
				<FilterRow visible={true} />
				<HeaderFilter visible={true} />
				<Column dataField="id" caption="ID" width={80} />
				<Column dataField="uuId" caption="UUID" width={100} />
				<Column dataField="paymentId" caption="PaymentId" width={140} />
				<Column
					dataField="type"
					caption="Тип"
					calculateCellValue={(data) => {
						switch (data.type) {
							case 1:
								return "Platinum 💎";
							case 2:
								return "Super 🚀";
							case 3:
								return "Чистый лист 🆕";
							case 4:
								return "Инкогнито";
							case 5:
								return "Повторная регистрация 🔄";
							default:
								return data.type;
						}
					}}
				/>

				<Column
					dataField="payType"
					caption="Метод"
					calculateCellValue={(data) => {
						switch (data.payType) {
							case 1:
								return "Подарок 🎁";
							case 2:
								return "ТБанк 🏦";
							case 3:
								return "Звёзды ⭐";
							default:
								return data.payType;
						}
					}}
					width={100}
				/>
				<Column dataField="price" caption="Цена" />
				<Column
					dataField="payState"
					caption="Оплата"
					width={100}
					alignment="center"
					cellRender={({ data }) =>
						data.payState ? (
							<span style={{ color: "#2e7d32", fontWeight: 600 }}>
								оплачен
							</span>
						) : (
							<span style={{ color: "#b2153f", fontWeight: 600 }}>
								не оплачен
							</span>
						)
					}
				/>
				<Column dataField="superLikes" caption="Суперлайки" />
				<Column dataField="payLink" caption="Ссылка" />
				<Column
					dataField="createDate"
					caption="Создан"
					dataType="date"
					format="dd.MM.yyyy"
				/>
				<Column
					dataField="endDate"
					caption="Окончание"
					dataType="date"
					format="dd.MM.yyyy"
				/>
				<Column
					caption="Действия"
					width={320}
					cellRender={({ data }) => {
						if (data.payType !== 2) return null;
						if (!data.paymentId) {
							return (
								<span style={{ color: "#999", fontSize: 12 }}>
									нет paymentId
								</span>
							);
						}
						return (
							<div style={{ display: "flex", gap: 6 }}>
								<Button
									text={checkingId === data.id ? "..." : "Проверить"}
									disabled={checkingId === data.id}
									onClick={() => handleCheck(data)}
									stylingMode="contained"
									type="default"
								/>
								{!data.payState ? (
									<Button
										text={reconcilingId === data.id ? "..." : "Подтвердить"}
										disabled={reconcilingId === data.id}
										onClick={() => handleReconcile(data)}
										stylingMode="contained"
										type="success"
									/>
								) : (
									<Button
										text={refundingId === data.id ? "..." : "Возврат"}
										disabled={refundingId === data.id}
										onClick={() => handleRefund(data)}
										stylingMode="contained"
										type="danger"
									/>
								)}
							</div>
						);
					}}
				/>
			</DataGrid>

			<div style={{ textAlign: "right", marginTop: 10 }}>
				<Button text="Закрыть" onClick={onClose} />
			</div>
		</Popup>
	);
};

export default OrderPopup;
